import styled from "styled-components/macro";
import { Color, theme } from "ui/theme";

const $SmallIcon = styled.svg`
  width: ${({ theme }) => theme.IconSmallSize};
  height: ${({ theme }) => theme.IconSmallSize};
`;

const $MediumIcon = styled.svg`
  width: ${({ theme }) => theme.IconMediumSize};
  height: ${({ theme }) => theme.IconMediumSize};
`;

const $LargeIcon = styled.svg`
  width: ${({ theme }) => theme.IconLargeSize};
  height: ${({ theme }) => theme.IconLargeSize};
`;

const $ExtraLargeIcon = styled.svg`
  width: ${({ theme }) => theme.IconExtraLargeSize};
  height: ${({ theme }) => theme.IconExtraLargeSize};
`;

type IconSize = "sm" | "md" | "lg" | "xl";

function elementFromSize(size: IconSize) {
  switch (size) {
    case "sm":
      return $SmallIcon;
    case "md":
      return $MediumIcon;
    case "lg":
      return $LargeIcon;
    case "xl":
      return $ExtraLargeIcon;
    default:
      throw new Error(`Invalid icon size: ${size}`);
  }
}

interface IconPropsBase {
  /**
   * The role of the icon, can be "img" which requires a title, or "presentation"
   * which will not be shown to screen readers
   */
  role: "img" | "presentation";
  /**
   * The svg data used to draw the image
   */
  path: string;
  /**
   * The size of the icon.  If not specified, defaults to `medium`
   */
  size?: IconSize;
  /**
   * The color of the icon. Default is Space500
   * TODO: Component Library: Once we have a better idea of the finalized icon designs, we can determine
   * if colors are a one off, or should follow a color theme similiar to typography
   */
  color?: keyof Color;
  /**
   * Configure the viewbox of the svg.
   * The default is '0 0 24 24'
   */
  viewBox?: string;
  /**
   * Configure the fill-rule of the svg.
   * The default is "inherit".
   */
  fillRule?: "inherit" | "nonzero" | "evenodd" | undefined;
  /**
   * Configure the clip-rule of the svg.
   * The default is "inherit".
   */
  clipRule?: "inherit" | "nonzero" | "evenodd" | undefined;
  className?: string;
}

interface PresentationalIconProps extends IconPropsBase {
  role: "presentation";
}

interface FunctionalIconProps extends IconPropsBase {
  role: "img";
  title: string;
}

export type SVGIconProps = PresentationalIconProps | FunctionalIconProps;

export type IconProps =
  | Omit<PresentationalIconProps, "path">
  | Omit<FunctionalIconProps, "path">;

const $Path = styled.path<{ $iconColor: string | null }>`
  fill: ${({ $iconColor }) =>
    $iconColor
      ? // !important is needed to override styling from Button for custom icon button colors
        `${$iconColor} !important`
      : ({ theme }) => theme.ColorSpace500};
`;

/**
 * Render arbitrary SVG path data, while enforcing good accessibility practices.
 *
 * If you're looking for specific icon components, look in the `src/ui/Icons` directory, instead.
 */
function SVGIcon(props: SVGIconProps) {
  const {
    path,
    viewBox = "0 0 24 24",
    size = "md",
    role,
    color,
    fillRule = "inherit",
    clipRule = "inherit",
    className,
  } = props;

  const SVGComponent = elementFromSize(size);
  const resolvedColor = color ? theme[color] : null;

  const PathElement = (
    <$Path
      d={path}
      $iconColor={resolvedColor}
      fillRule={fillRule}
      clipRule={clipRule}
    />
  );
  const svgCommonProps = {
    viewBox,
    role,
    className,
  };

  if (props.role === "img") {
    return (
      <SVGComponent {...svgCommonProps} aria-label={props.title}>
        {PathElement}
      </SVGComponent>
    );
  } else {
    return (
      <SVGComponent {...svgCommonProps} aria-hidden>
        {PathElement}
      </SVGComponent>
    );
  }
}

export default SVGIcon;
